<script setup lang="ts">
import { cartMaxPayByBonusesCreate, orgUnitCartCalculateDiscountCreate, type PaymentProviderType } from "@innoclouds/api";
import { DecorIcon } from "@innoclouds/ui";
import {
	CART_BACK_DEFAULT,
	CART_COMMENT_DEFAULT,
	CART_LOYALTY_DEFAULT,
	CART_PAY_DEFAULT,
	type CartBackControl,
	type CartCommentControl,
	type CartLoyaltyControl,
	type CartPayControl,
	cartBackVariants,
	cartCommentVariants,
	cartLoyaltyVariants,
	cartPayVariants,
} from "@innoclouds/ui/templates/cart/cartControls/cartControls.ts";
import { computedAsync, useElementSize } from "@vueuse/core";
import axios from "axios";
import { useTranslation } from "i18next-vue";
import { animate, motion } from "motion-v";
import { storeToRefs } from "pinia";
import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch, watchEffect } from "vue";
import { useRoute, useRouter } from "vue-router";
import cartIcon from "#/assets/icons/cart_icon.svg";
import chatIcon from "#/assets/icons/chat.svg";
import chevronLeftIcon from "#/assets/icons/chevron_left.svg";
import crossIcon from "#/assets/icons/cross.svg";
import qrScannerIcon from "#/assets/icons/qr_scanner.svg";
import ArrowLeft from "#/assets/iconsDynamic/ArrowLeft.vue";
import CartBox from "#/components/CartBox/CartBox.vue";
import CardReader from "#/components/cardReader/CardReader.vue";
import CartCompletedOrder from "#/components/cartCompletedOrder/CartCompletedOrder.vue";
import CartPayment from "#/components/cartPayment/CartPayment.vue";
import KsoCheckout from "#/components/ksoCheckout/KsoCheckout.vue";
import { DEFAULT_LOADER_MIN_DURATION, type KsoStage } from "#/components/ksoCheckout/stages";
import Locator from "#/components/Locator/Locator.vue";
import ModalCartAdultConfirm from "#/components/modal/CartAdultConfirm.vue";
import ModalCartAdultReset from "#/components/modal/CartAdultReset.vue";
import ModalCartDeleteConfirm from "#/components/modal/CartDeleteConfirm.vue";
import ModalCartExtendConfirm from "#/components/modal/CartExtendConfirm.vue";
import ModalCartPromoResetConfirm from "#/components/modal/CartPromoResetConfirm.vue";
import ModalCartRequiredEmail from "#/components/modal/CartRequiredEmail.vue";
import ModalLoyaltyPlantScanner from "#/components/modal/LoyaltyPlantScanner.vue";
import NightScanEmpty from "#/components/nightScanEmpty/NightScanEmpty.vue";
import OrderBox from "#/components/OrderBox/OrderBox.vue";
import PromoField from "#/components/promoField/PromoField.vue";
import SystemNotification from "#/components/SystemNotification/SystemNotification.vue";
import { type CreateOrderParams, useCreateOrder } from "#/composables/useCreateOrder";
import { clearSavedData, initPaymentLink, resetPaymentSocket, restoreCart, restoreUserData } from "#/payment/handlers/basePaymentHandler";
import { InitConnection } from "#/requests";
import PopupWindowDefault from "#/semantics/windows/popupWindowDefault/PopupWindowDefault.vue";
import { useCartStore } from "#/store/cart";
import { useDecorationStore } from "#/store/decoration";
import { useMainStore } from "#/store/main";
import { useModalStore } from "#/store/modals";
import { useScheduleStore } from "#/store/schedule";
import { useTableStore } from "#/store/table";
import { useThemeStore } from "#/store/theme";
import { useUserStore } from "#/store/user";
import BottomSheet from "#/ui/bottomSheet/BottomSheet.vue";
import ButtonDefault from "#/ui/buttons/default/ButtonDefault.vue";
import NotificationWindow from "#/ui/notificationWindow/NotificationWindow.vue";
// import QRious from "qrious";
import AnimNum from "#/ui/recountNumber/RecountNumber.vue";
import { applyOutOfStockToCart, type OutOfStockItem } from "#/utils/applyOutOfStock";
import { formatDeliveryCost } from "#/utils/delivery";
import { formatPrice } from "#/utils/i18next";
import { addNutritions, calculateCartItemNutritions, calculateOrderItemNutritions, emptyNutritions } from "#/utils/nutritions";

/** Strips C0 control chars: \0 \a \b \t \n \v \f \r \v */
// biome-ignore lint/suspicious/noControlCharactersInRegex: Это корректный набор для проверки чтобы исключить непечатаемые символы
const COMMENT_CONTROL_CHARS = /[\x00\x07\x08\x09\x0A\x0B\x0C\x0D]/g;
const REQUIRED_EMAIL_PROVIDERS = ["YOOKASSA_BASE", "YOOKASSA", "SBP"];

const connection = InitConnection.create();

let abortController: AbortController | null;

const router = useRouter();
const route = useRoute();
const { t } = useTranslation();

const storeMain = useMainStore();
const storeCart = useCartStore();
const themeStore = useThemeStore();
const tableStore = useTableStore();
const modals = useModalStore();
const schedule = useScheduleStore();
const userStore = useUserStore();

const props = defineProps<{
	configPage: {
		kiosk_menu: boolean;
		qr_menu: boolean;
		data: any;
		textNotificationCart: any;
	};
	textPaymentBtn: string;
	config_qr: boolean;
	decoration?: string;
	ksoDock?: string;
	loyaltyAction?: () => void;
}>();

const emit = defineEmits<{
	paying: [value: boolean];
}>();

const decor = useDecorationStore().screen(props.decoration);

const { initialCartPrice_lib, totalPrice_lib, countBonus, config_lib, cartProducts_lib } = storeToRefs(storeMain);

const {
	isButtonDisabled,
	orderCompleted_lib,
	extraDiscount,
	is_notification_lib,
	isCreateButtonVisible,
	is_paymentCart,
	providerTypes,
	notificationData_lib,
	selectedProvider,
	configPayment,
} = storeToRefs(storeCart);

const isNightEmpty = computed(
	() => props.configPage.kiosk_menu && schedule.isNight && !cartProducts_lib.value.length && !(tableStore.hasOrder && tableStore.order?.items?.length),
);

const isKsoFlow = computed(() => !!props.configPage.kiosk_menu && !tableStore.isTableService && !!themeStore.data?.loader_screen?.type);
const isKsoCreating = ref(false);
const ksoBonusesRefreshTick = ref(0);
const isKsoMinDurationPassed = ref(false);
let ksoMinDurationTimer: ReturnType<typeof setTimeout> | null = null;

const loyaltyContent = useTemplateRef<{ sticker?: HTMLElement | null }>("loyaltyContent");
const stickerLoyality = computed(() => loyaltyContent.value?.sticker ?? null);
const cartCardsEl = useTemplateRef<HTMLElement>("cartCardsEl");
const isBonusActive = ref(false);
const disabledDiscountBtn = ref<boolean>(false);
const isBonusDecimalAllowed = ref(false);

const promoCode = ref("");
const isLoadingPromoCode = ref(false);
const commentEl = useTemplateRef("commentEl");
const comment = computed({
	get: () => storeMain.orderComment,
	set: (value: string) => storeMain.setOrderComment(value),
});
const isCommentInvalid = ref(false);
const is_ToggleComment_kso = ref(false);
const is_ToggleComment_qr = ref(false);
const isLocatorSheetOpen = ref(false);
const COMMENT_MAX_LENGTH = 50;
const COMMENT_LOCATOR_LENGTH = 4;

const discountSumma = ref(0);
const updateTrigger = ref(0);
const isOrderItemsVisible = ref(true);

const orderId = ref();
const hasPromoCode = computed(() => !!themeStore.data?.cart_promo_code && promoCode.value?.length > 0 && !tableStore.isTableService);
const qrType = computed(() => (!props.configPage.qr_menu ? providerTypes.value[0]?.front_type : "YOOKASSA_BASE"));
const qrTypeRms = computed(() => (!props.configPage.qr_menu ? providerTypes.value[0]?.type : "YOOKASSA"));
const formattedComment = computed(() => comment.value.replace(COMMENT_CONTROL_CHARS, " "));
const buttonText = computed(() => {
	if ([t(`cart.button.auth-required`).toUpperCase()].includes(props.textPaymentBtn.toUpperCase())) return props.textPaymentBtn;
	if (tableStore.isTableService && tableStore.hasOrder) return t(`cart.button.extend-order`);
	if (props.configPage.kiosk_menu)
		return isCreateButtonVisible.value ? themeStore.data?.kiosk_create_order_button_text || t(`cart.button.go-to-pay-order`) : props.textPaymentBtn;
	return isCreateButtonVisible.value ? t(`cart.button.create-order`) : props.textPaymentBtn;
});
const isLocatorEnabled = computed(() => {
	if (!props.configPage.kiosk_menu) return false;
	return themeStore.data?.kiosk_enable_locators !== false;
});

const { width: stickerLoyalityWidth } = useElementSize(stickerLoyality, { width: 0, height: 0 }, { box: "border-box" });

const deliveryCost = computed(() => formatDeliveryCost(tableStore.data?.branch?.delivery_cost));

const totalNutritions = computed(() => {
	let total = emptyNutritions();

	if (tableStore.isTableService && tableStore.order?.items?.length) {
		for (const item of tableStore.order.items) {
			total = addNutritions(total, calculateOrderItemNutritions(item));
		}
	}

	for (const item of cartProducts_lib.value) {
		total = addNutritions(total, calculateCartItemNutritions(item));
	}

	return total;
});

const generalComment = computed(() => {
	if (themeStore.data?.comments) {
		const baseComments = themeStore.data.comments;
		return {
			fieldName: props.configPage.kiosk_menu ? baseComments.kiosk_comment_field_name : baseComments.qr_comment_field_name,
			placeholder: props.configPage.kiosk_menu ? baseComments.kiosk_comment_field_placeholder : baseComments.qr_comment_field_placeholder,
			show: props.configPage.kiosk_menu ? baseComments.kiosk_show_order_comment : baseComments.qr_show_order_comment,
		};
	}
});

const maxBonuses = computedAsync(
	async (onCancel) => {
		const abortController = new AbortController();
		onCancel(() => abortController.abort());

		const isBonusesRequestAllowed = isKsoFlow.value
			? ksoBonusesRefreshTick.value >= 0 && !isCreateButtonVisible.value && !isButtonDisabled.value && !!orderId.value
			: isBonusActive.value;
		if (!isBonusesRequestAllowed || !userStore.data?.balance || !totalPrice_lib.value) return 0;

		const response = await cartMaxPayByBonusesCreate({ order_id: orderId.value }, { signal: abortController.signal }).catch(() => null);
		if (!response?.by_bonuses) return 0;
		if (userStore.isAuthorized && "balance" in response && response.balance !== userStore.data?.balance) await userStore.invalidate();

		isBonusDecimalAllowed.value = response.non_integers_allowed || false;

		return Math.round(Math.min(response.by_bonuses, userStore.data?.balance, totalPrice_lib.value) * 100) / 100;
	},
	0,
	{ shallow: true },
);

const goBack = () => router.push(`/${route.params.orgId}/`);

const preventNonNumeric = (e: { key: string; preventDefault: () => void }) => {
	const excludeSymbols = isBonusDecimalAllowed.value ? ["a", "A", "+", "-", "."] : ["a", "A", "+", "-", ".", ","];
	if (excludeSymbols.includes(e.key)) e.preventDefault();
};

const deleteItem = (idSystem: string) => {
	resetPromoCode();
	fullDiscountReset();
	storeMain.deleteItemFromCart(idSystem);
	if (cartProducts_lib.value.length === 0) {
		is_ToggleComment_kso.value = false;
		is_ToggleComment_qr.value = false;
		if (tableStore.hasOrder) router.replace({ name: "order-ts" });
	}
};

const isCommentLocked = computed(() => !isCreateButtonVisible.value);

watch(isCommentLocked, (locked) => {
	if (!locked) return;
	is_ToggleComment_kso.value = false;
	is_ToggleComment_qr.value = false;
});

const saveCommentKso = () => {
	is_ToggleComment_kso.value = !is_ToggleComment_kso.value;
	if (comment.value.length === 0) return;
};

const isCommentOverlay = computed(() => decor.variant("comment_editor", "inline") === "overlay");
const isCommentAlwaysOpen = computed(() => isCommentRequired.value && !isCommentOverlay.value);
const commentTitle = computed(() => generalComment.value?.fieldName || t(`cart.comment.title`));
const commentChips = computed(() => (decor.visible("comment_chips", false) ? (storeCart.orderItemCommentSuggestions ?? []) : []));

const closeCommentEditor = () => {
	commentEl.value?.blur();
	is_ToggleComment_kso.value = false;
};

const onCommentEnter = (event: KeyboardEvent) => {
	if (!isCommentOverlay.value) return;
	event.preventDefault();
	closeCommentEditor();
};

const appendCommentChip = (chip: string) => {
	const separator = comment.value && !/[\s,]$/.test(comment.value) ? ", " : "";
	const next = `${comment.value}${separator}${chip}`;
	if (next.length > commentMaxLength.value) return;
	comment.value = next;
};

watch(is_ToggleComment_kso, (open) => {
	if (open && isCommentOverlay.value) nextTick(() => commentEl.value?.focus());
});

const truncatedLoyaltyText = computed(() => {
	if (userStore.data) {
		const userName = userStore.data?.first_name?.trim() || userStore.data?.phone;
		if (userName) return userName.replace(/\S+/g, (w: string) => (w.length > 15 ? `${w.slice(0, 15)}...` : w));
	}

	if (storeCart.tabName) return storeCart.tabName?.trim()?.replace(/\S+/g, (w: string) => (w.length > 15 ? `${w.slice(0, 15)}...` : w));

	return themeStore.data?.cart_loyalty_button?.cart_loyalty_button_text || t(`cart.button.loyalty`);
});

const saveCommentQr = () => {
	is_ToggleComment_qr.value = !is_ToggleComment_qr.value;
	if (comment.value.length === 0) return;
};

const validateInput = () => {
	let newCount: number = isBonusDecimalAllowed.value ? +countBonus.value.toFixed(2) : Math.floor(countBonus.value) || 0;
	if (newCount > maxBonuses.value) newCount = maxBonuses.value;
	if (newCount !== countBonus.value) storeMain.setBonus(newCount);
};

watchEffect(() => {
	validateInput();
	storeMain.setBonus(totalPrice_lib.value === 0 ? 0 : isBonusDecimalAllowed.value ? +countBonus.value.toFixed(2) : Math.floor(countBonus.value));
});

const serializeItemModifier = (m: any): any[] => {
	const codes: string[] = Array.isArray(m?.marking_codes) ? m.marking_codes.filter(Boolean) : [];
	if (m?.is_marked && codes.length > 0) {
		return codes.map((code) => {
			const { marking_codes, ...rest } = m;
			return { ...rest, amount: 1, marking_code: code };
		});
	}
	const { marking_codes, ...rest } = m ?? {};
	return [{ ...rest, ...(m?.marking_code ? { marking_code: m.marking_code } : {}) }];
};

const clearCartItems = () => {
	return cartProducts_lib.value
		.filter((item: any) => !item.is_combo && !item.isCombo)
		.map((item: any) => ({
			comment: item.comment ?? null,
			id: item.id,
			front_item_id: item.idSystem || item.id,
			is_fake: !!item.is_fake,
			adult: !!item.adult,
			is_marked: !!item.is_marked,
			is_od_united_product: !!item.is_od_united_product,
			removals: item.removals ?? null,
			amount: item.amount,
			size: item.size ?? null,
			...(item.marking_code ? { marking_code: item.marking_code } : {}),
			modifiers: (item.modifiers ?? []).flatMap(serializeItemModifier),
			group_modifiers: (item.group_modifiers ?? []).map((g: any) => ({
				...g,
				modifiers: (g?.modifiers ?? []).flatMap(serializeItemModifier),
			})),
		}));
};

const clearCartCombos = () => {
	return cartProducts_lib.value
		.filter((item: any) => item.is_combo === true || item.isCombo === true)
		.map((combo: any) => {
			let groupsSource = combo.combos?.[0]?.groups || combo.groups || [];

			const groupsPayload: any[] = [];

			groupsSource.forEach((group: any) => {
				if (!group?.id) return;

				const itemData = group.item || group;

				if (!itemData?.id) {
					console.warn(`[clearCartCombos] Нет item.id в группе ${group.id}`);
					return;
				}

				const sizeId = itemData.size?.id ?? itemData.size ?? null;

				const explodeComboModifier = (m: any): any[] => {
					const codes: string[] = Array.isArray(m?.marking_codes) ? m.marking_codes.filter(Boolean) : [];
					if (m?.is_marked && codes.length > 0) {
						return codes.map((code) => ({ id: m.id, amount: 1, marking_code: code }));
					}
					return [{ id: m.id, amount: m.amount || 1, ...(m.marking_code ? { marking_code: m.marking_code } : {}) }];
				};

				const modifiersPayload = (itemData.modifiers || []).filter((m: any) => (m.amount ?? 0) > 0).flatMap(explodeComboModifier);

				const groupModifiersPayload: any[] = [];

				if (Array.isArray(itemData.group_modifiers)) {
					itemData.group_modifiers.forEach((gmod: any) => {
						const groupId = gmod.group || gmod.id;
						if (!groupId) return;

						const mods = (gmod.modifiers || []).filter((m: any) => (m.amount ?? 0) > 0).flatMap(explodeComboModifier);

						if (mods.length > 0) {
							groupModifiersPayload.push({
								group: groupId,
								modifiers: mods,
							});
						}
					});
				}

				const itemPayload = {
					id: itemData.id,
					amount: 1,
					size: sizeId,
					...(itemData.marking_code ? { marking_code: itemData.marking_code } : {}),
					...(modifiersPayload.length > 0 && { modifiers: modifiersPayload }),
					...(groupModifiersPayload.length > 0 && { group_modifiers: groupModifiersPayload }),
				};

				groupsPayload.push({
					id: group.id,
					item: itemPayload,
				});
			});

			return {
				id: combo.id,
				front_item_id: combo.idSystem || combo.id,
				amount: combo.amount || 1,
				groups: groupsPayload,
			};
		});
};

const findMissingMark = () => {
	for (const it of cartProducts_lib.value as any[]) {
		if (it?.is_marked && !it.marking_code) return it;
		const mods = [...(it?.modifiers ?? []), ...(it?.group_modifiers ?? []).flatMap((g: any) => g?.modifiers ?? [])];
		for (const m of mods) {
			if (m?.is_marked && !(Array.isArray(m.marking_codes) && m.marking_codes.length)) return m;
		}
	}
	return null;
};

const hasMissingMark = () => {
	if (!findMissingMark()) return false;
	storeCart.showNotification(t(`marking.popup.title`));
	return true;
};

const { makeCreateOrderRequest } = useCreateOrder();

const paymentCartItems = computed(() => clearCartItems());
const commentMaxLength = computed(() => {
	if (props.configPage.kiosk_menu && themeStore.data?.kiosk_enable_locators) {
		return COMMENT_MAX_LENGTH - COMMENT_LOCATOR_LENGTH - (themeStore.data?.kiosk_locator_prefix?.length || 0);
	}

	return COMMENT_MAX_LENGTH;
});
const isCommentRequired = computed(
	() =>
		(props.configPage.qr_menu && themeStore.data?.comments?.qr_is_order_comment_required) ||
		(props.configPage.kiosk_menu && themeStore.data?.comments?.kiosk_is_order_comment_required),
);
const notifyRequiredComment = () => {
	const isMissing = isCommentRequired.value && comment.value?.trim()?.length < 1 && !(tableStore.isTableService && tableStore.hasOrder);
	if (isMissing && isCommentOverlay.value && !isCommentLocked.value && !commentEl.value) {
		storeCart.showNotification(t(`cart.notification.comment.required`));
		isCommentInvalid.value = true;
		is_ToggleComment_kso.value = true;
		return true;
	}
	if (!(isCommentRequired.value && comment.value?.trim()?.length < 1 && commentEl.value) || (tableStore.isTableService && tableStore.hasOrder)) return false;

	storeCart.showNotification(t(`cart.notification.comment.required`));
	commentEl.value.focus();
	isCommentInvalid.value = true;
	animate(commentEl.value.parentElement!, { x: [0, -10, 0, 10, 0] }, { duration: 0.1, repeat: 2 });
	return true;
};

const makeInitRequest = (orderId: string, providerType: string, email?: string) => {
	isButtonDisabled.value = true;
	const finalPrice = totalPrice_lib.value + deliveryCost.value - countBonus.value;

	storeCart.setCashPaymentAmount(finalPrice);

	const extra: Record<string, unknown> = {};
	if (deliveryCost.value !== null) extra["delivery_cost"] = deliveryCost.value;
	if (email) extra["email"] = email;

	initPaymentLink(orderId, providerType, extra).then((response) => {
		if (configPayment.value) {
			if (response.url && props.configPage.qr_menu) {
				storeCart.tabName = null;
				window.location.replace(response.url);
			}
		}
	});
};

const openPaymentBox = async () => {
	if (props.configPage.qr_menu && !userStore.isAuthorized && themeStore.data?.qr_auth_required !== false) {
		router.push(`/${route.params.orgId}/register`);
		return;
	}

	if (isCommentRequired.value && notifyRequiredComment()) return;

	if (hasMissingMark()) return;

	if (tableStore.isTableService) {
		if (tableStore.availableExtend || tableStore.hasOrder) {
			storeCart.showNotification(t(`cart.notification.order.already-created`));
			tableStore.refresh();
			return;
		}

		if (!tableStore.hasOrder) {
			try {
				isButtonDisabled.value = true;
				await connection.confirmOrder(orderId.value);
				storeCart.deleteCart();
				tableStore.selectOrder(orderId.value);
				tableStore.refresh();
				router.push({ name: "order-ts" });
			} catch (error: any) {
				storeCart.showNotification(error?.response?.data?.user_friendly_message || t(`cart.notification.order.create-error`));
				tableStore.refresh();
				isButtonDisabled.value = false;
				resetCart();
			}
		} else {
			modals.open("cart-extend-confirm");
		}

		return;
	}

	if (props.configPage.qr_menu || providerTypes.value.length <= 1) {
		if (!qrTypeRms.value) return storeCart.showNotification(t(`cart.notification.payment.unavailable`));
		await handleSelectPayment(qrTypeRms.value);
		return;
	}

	is_paymentCart.value = !is_paymentCart.value;
	return;
};

const handleSelectPayment = (providerType: string) => {
	is_paymentCart.value = false;
	if (!providerType || !orderId.value || isCreateButtonVisible.value) return;
	if (!REQUIRED_EMAIL_PROVIDERS.includes(providerType.toUpperCase()) || userStore.data?.email?.length) return makeInitRequest(orderId.value!, providerType);

	modals.open("cart-required-email", { orderId: orderId.value!, providerType: providerType });
};

const calculateDiscounts = async (onlyPromo: boolean = false) => {
	if (onlyPromo) isLoadingPromoCode.value = true;
	extraDiscount.value = 0;
	discountSumma.value = 0;
	const payload: Record<string, unknown> = {
		items: [...clearCartItems()],
		comment: formattedComment.value,
		combos: clearCartCombos(),
		promo_code: promoCode.value.length > 0 ? promoCode.value : undefined,
	};

	if (tableStore.id) payload.table = tableStore.id;
	if (storeCart.selectedOrderType?.id) payload.order_type_id = storeCart.selectedOrderType?.id;

	const params: Record<string, unknown> = {};
	if (onlyPromo) params.only_promo = true;

	try {
		disabledDiscountBtn.value = true;
		const response = await orgUnitCartCalculateDiscountCreate(config_lib.value?.pointId, payload, params, { signal: abortController?.signal });
		if (!cartProducts_lib.value) return;

		const responseItems: any[] = Array.isArray(response?.items) ? response.items : [];
		const responseCombos: any[] = Array.isArray(response?.combos) ? response.combos : [];

		cartProducts_lib.value.forEach((currentItem) => {
			const existingItem: any = responseItems.find((discItem: any) => discItem.front_item_id === currentItem.idSystem);

			if (existingItem) {
				currentItem.group_modifiers.forEach((group: any) => {
					const existingGroup: any = existingItem.group_modifiers.find((discGroup: any) => discGroup.group === group.group);

					if (existingGroup) {
						group.modifiers.forEach((modifier: any) => {
							const existingModifier: any = existingGroup.modifiers.find((discModifier: any) => discModifier.id === modifier.id);

							if (existingModifier && Number(modifier.price) !== Number(existingModifier?.new_price)) {
								const discount = (existingModifier.discounts || []).reduce((sum: number, item: any) => (sum += +item.discount), 0);

								discountSumma.value += discount;
								modifier.discounts = existingModifier.discounts || [];
								modifier.delta_price = Number(existingModifier.delta_price);
								modifier.new_price = modifier.price;
								modifier.price = Number(existingModifier?.new_price);
								modifier.total_new_price = Number(existingModifier.total_new_price);
							}
						});
					}
				});

				currentItem.modifiers.forEach((modifier: any) => {
					const existingModifier: any = existingItem.modifiers.find((discModifier: any) => discModifier.id === modifier.id);

					if (existingModifier && Number(modifier.price) !== Number(existingModifier?.new_price)) {
						const discount = (existingModifier.discounts || []).reduce((sum: number, item: any) => (sum += +item.discount), 0);

						discountSumma.value += discount;
						modifier.discounts = existingModifier.discounts || [];
						modifier.delta_price = Number(existingModifier.delta_price);
						modifier.new_price = modifier.price;
						modifier.price = Number(existingModifier?.new_price);
						modifier.total_new_price = Number(existingModifier.total_new_price);
					}
				});

				if (currentItem.price !== Number(existingItem.new_price)) {
					const discount = (existingItem.discounts || []).reduce((sum: number, item: any) => (sum += +item.discount), 0);

					discountSumma.value = discountSumma.value + discount;
					currentItem.new_price = currentItem.price;
					currentItem.price = existingItem.new_price;
					currentItem.discounts = existingItem.discounts;
					currentItem.total_new_price = Number(existingItem.total_new_price);
				}
			}
		});

		const comboCartItems = cartProducts_lib.value.filter((item: any) => item.is_combo);

		comboCartItems.forEach((comboItem: any, index: number) => {
			const existingCombo: any = responseCombos[index];
			if (!existingCombo) return;

			const comboDiscount = (existingCombo.discounts || []).reduce((sum: number, disc: any) => (sum += Number(disc.discount) || 0), 0);

			if (comboDiscount !== 0) {
				discountSumma.value += comboDiscount;
			}

			comboItem.new_price = comboItem.price;
			comboItem.price = Number(existingCombo.total_combo_price ?? comboItem.price);
			comboItem.total_new_price = Number(existingCombo.total_combo_price ?? comboItem.total_new_price ?? comboItem.price);
			comboItem.discounts = existingCombo.discounts || [];
		});

		cartProducts_lib.value = [...cartProducts_lib.value];
		totalPrice_lib.value = response.total_cart_price ?? 0;
		if (onlyPromo) isLoadingPromoCode.value = false;
	} catch (error: any) {
		if (onlyPromo) isLoadingPromoCode.value = false;
		disabledDiscountBtn.value = false;
		if (!axios.isCancel(error)) {
			const responseData = error?.response?.data as Record<string, any>;
			if (responseData?.extra?.action === "RemoveOutOfStockItems") {
				const outOfStockItems: OutOfStockItem[] = responseData?.extra?.data?.out_of_stock_items ?? [];
				applyOutOfStockToCart(cartProducts_lib.value, outOfStockItems);
				storeMain.updateCartInLocalStorage();
				storeCart.showNotification(responseData?.user_friendly_message || t(`cart.notification.combos.error`));
			} else {
				storeCart.showNotification(responseData?.user_friendly_message || t(`cart.notification.discount.error`));
			}
			throw error;
		}
	}
};

const extendOrder = async () => {
	let oldState = isButtonDisabled.value;
	isButtonDisabled.value = true;
	try {
		await connection.extendOrder([...clearCartItems()]);
		storeCart.deleteCart();
		tableStore.refreshOrder();
		router.push({ name: "order-ts" });
	} catch (error: any) {
		storeCart.showNotification(error?.response?.data?.user_friendly_message || t(`cart.notification.order.extend-error`));
		throw error;
	} finally {
		isButtonDisabled.value = oldState;
	}
};

const createOrder = async (locatorNumber?: string) => {
	if (isCommentRequired.value && notifyRequiredComment()) return;

	if (hasMissingMark()) return;

	if (!qrType.value) {
		storeCart.showNotification(t(`cart.notification.payment.unavailable`));
		isButtonDisabled.value = false;
		return;
	}

	orderId.value = null;
	if (abortController) abortController?.abort();
	abortController = new AbortController();
	isButtonDisabled.value = true;
	startKsoCreating();

	try {
		await calculateDiscounts();

		selectedProvider.value = { front_type: qrType };

		const params: CreateOrderParams = {
			paymentMethodId: qrType.value,
			items: paymentCartItems.value,
			combos: clearCartCombos(),
			comment: locatorNumber ? formattedComment.value.slice(0, commentMaxLength.value) : formattedComment.value,
			signal: abortController?.signal,
			locator: locatorNumber || undefined,
			promo_code: promoCode.value.length > 0 ? promoCode.value : undefined,
		};

		const createRequest = await makeCreateOrderRequest({ ...props.configPage, resetDiscounts: resetCart }, params);
		orderId.value = createRequest?.data?.id;
		isCreateButtonVisible.value = false;
	} catch (error) {
		if (!axios.isCancel(error)) console.error("[cart](createOrder)", error);
		if (!axios.isCancel(error) && tableStore.isTableService) tableStore.refresh();
		isButtonDisabled.value = false;
		stopKsoCreating();
		resetCart();
	}
};

const buttonAction = () => {
	if (
		props.configPage.qr_menu &&
		[t(`cart.button.auth-required`).toUpperCase()].includes(props.textPaymentBtn.toUpperCase()) &&
		!userStore.isAuthorized &&
		themeStore.data?.qr_auth_required !== false
	) {
		return router.push(`/${route.params.orgId}/register`);
	}

	if (tableStore.isTableService && tableStore.availableExtend) return modals.open("cart-extend-confirm");
	if (
		props.configPage.kiosk_menu &&
		themeStore.data?.kiosk_age_confirmation_enabled &&
		!storeCart.isAdultConfirmed &&
		cartProducts_lib.value.some((product) => product.adult)
	)
		return modals.open("cart-adult-confirm");
	storeCart.snapshotMarkedItems();

	if (isCreateButtonVisible.value) {
		if (isCommentRequired.value && notifyRequiredComment()) return;

		if (!isLocatorEnabled.value) {
			return createOrder();
		}
		isLocatorSheetOpen.value = true;
		return;
	}

	return openPaymentBox();
};

const toggleBonuses = () => {
	if (userStore.data?.balance && userStore.data.balance > 0) {
		isBonusActive.value = !isBonusActive.value;
	} else {
		storeCart.showNotification(t(`cart.notification.bonus.empty`));
	}
};

const isKsoOrderReady = computed(() => !isCreateButtonVisible.value && !isButtonDisabled.value && !!orderId.value);

const ksoStage = computed<KsoStage>(() => {
	if (!isKsoFlow.value || orderCompleted_lib.value) return "hidden";
	if (isKsoCreating.value) return "creating";
	if (isCreateButtonVisible.value || !orderId.value) return "hidden";
	if (is_paymentCart.value) return "methods";
	return "created";
});

const ksoDiscount = computed(() => (extraDiscount.value !== 0 ? extraDiscount.value : discountSumma.value));
const isPaying = computed(() => ksoStage.value !== "hidden");
watch(isPaying, (value) => emit("paying", value), { immediate: true });
const summarySubtotal = computed(() => (totalPrice_lib.value ?? 0) - ksoDiscount.value);
const summaryPercent = computed(() => (summarySubtotal.value > 0 ? Math.round((Math.abs(ksoDiscount.value) / summarySubtotal.value) * 100) : 0));
const ksoTotal = computed(() => {
	const value = Math.round(((totalPrice_lib.value ?? 0) + deliveryCost.value - countBonus.value) * 100) / 100;
	return value === 0 ? 0 : value;
});
const ksoBonusBalance = computed(() => (userStore.isAuthorized ? (userStore.data?.balance ?? 0) : 0));
const isKsoBonusesAvailable = computed(() => userStore.isAuthorized && ksoBonusBalance.value > 0 && !tableStore.isTableService);

const stopKsoCreating = () => {
	if (ksoMinDurationTimer) {
		clearTimeout(ksoMinDurationTimer);
		ksoMinDurationTimer = null;
	}
	isKsoCreating.value = false;
	isKsoMinDurationPassed.value = false;
	storeCart.isOrderCreating = false;
};

const startKsoCreating = () => {
	if (!isKsoFlow.value) return;

	isKsoCreating.value = true;
	isKsoMinDurationPassed.value = false;
	storeCart.isOrderCreating = true;

	if (ksoMinDurationTimer) clearTimeout(ksoMinDurationTimer);
	ksoMinDurationTimer = setTimeout(() => {
		isKsoMinDurationPassed.value = true;
		ksoMinDurationTimer = null;
	}, DEFAULT_LOADER_MIN_DURATION);
};

watch([isKsoOrderReady, isKsoMinDurationPassed], ([ready, elapsed]) => {
	if (isKsoCreating.value && ready && elapsed) stopKsoCreating();
});

watch(isCreateButtonVisible, (visible) => {
	if (visible && isKsoCreating.value) stopKsoCreating();
});

const applyKsoBonuses = (value: number) => {
	storeMain.setBonus(value);
};

const cancelKsoCreating = () => {
	abortController?.abort();
	isButtonDisabled.value = false;
	stopKsoCreating();
	resetCart();
};

const cancelPayment = () => {
	abortController?.abort();
	abortController = null;

	const config = configPayment.value;
	resetPaymentSocket();
	if (config) storeCart.isAdultConfirmed = storeCart.isAdultConfirmed || restoreCart(config);
	else storeMain.loadCart();
	restoreUserData();
	clearSavedData();

	orderCompleted_lib.value = null;
	orderId.value = null;
	isButtonDisabled.value = false;
	is_paymentCart.value = false;
	selectedProvider.value = undefined;
	storeCart.linkQR = "";
	stopKsoCreating();
	resetCart();
	isCreateButtonVisible.value = true;
};

const selectKsoProvider = (provider: PaymentProviderType) => {
	if (!provider?.type) return;
	selectedProvider.value = provider;
	handleSelectPayment(provider.type);
};

onMounted(async () => {
	storeMain.loadCart();
	orderCompleted_lib.value = null;
	totalPrice_lib.value = null;
	extraDiscount.value = 0;
	resetCart();

	if (!userStore.isAuthorized && themeStore.data?.kiosk_login_loyalty_reminder_in_cart_text) {
		storeCart.showNotification(themeStore.data?.kiosk_login_loyalty_reminder_in_cart_text);
	}
});

onUnmounted(() => {
	if (abortController) {
		abortController?.abort();
		abortController = null;
	}
	stopKsoCreating();
	isButtonDisabled.value = false;
	orderId.value = null;
	orderCompleted_lib.value = null;
	totalPrice_lib.value = null;
	extraDiscount.value = 0;
	cartProducts_lib.value.splice(0, cartProducts_lib.value.length, ...cartProducts_lib.value.map(storeMain.makeProductFullyReactive));
});

const minOrderPrice = computed(() => {
	if (props.configPage.qr_menu) {
		if (tableStore.isTableService && tableStore.hasOrder) return 0;
		if (tableStore.data?.branch) return tableStore.data?.branch?.qr_order_minimum_price ?? config_lib.value?.qr_order_minimum_price ?? 0;

		return config_lib.value?.qr_order_minimum_price || 0;
	}
	if (props.configPage.kiosk_menu) return config_lib.value?.kiosk_order_minimum_price || 0;
	return 0;
});

const disabledStateBtn = computed(() => {
	if ([t(`cart.button.auth-required`).toUpperCase()].includes(props.textPaymentBtn.toUpperCase())) return false;
	if (tableStore.isTableService && tableStore.hasOrder && !tableStore.availableExtend) return true;
	if (minOrderPrice.value > initialCartPrice_lib.value && !tableStore.hasOrder) return true;
	if (tableStore?.data?.id && schedule.isClosed) return true;
	if (disabledDiscountBtn.value) return true;
	if (isButtonDisabled.value) return true;
	if (cartProducts_lib.value.length === 0) return true;

	orderCompleted_lib.value = null;
	return isButtonDisabled.value;
});

const isCartEmpty = computed(() => !cartProducts_lib.value.length && !(tableStore.hasOrder && tableStore.order?.items?.length) && !isNightEmpty.value);
const finalTotal = computed(() => {
	if (tableStore.isTableService && tableStore.order) return (tableStore.order.total_cost ?? 0) + totalPrice_lib.value;
	return totalPrice_lib.value + (deliveryCost.value ?? 0) - (Number(countBonus.value) || 0);
});
const isCommentLineVisible = computed(
	() =>
		decor.visible("comment_line", false) &&
		!!props.configPage.kiosk_menu &&
		!!generalComment.value?.show &&
		!isCommentAlwaysOpen.value &&
		!is_ToggleComment_kso.value &&
		comment.value.length > 0 &&
		cartProducts_lib.value.length > 0,
);

const backContent = computed(() => cartBackVariants[decor.variant("back_button", CART_BACK_DEFAULT)] ?? cartBackVariants[CART_BACK_DEFAULT]);
const backControl = computed((): CartBackControl => ({ label: t(`cart.button.back`), icon: decor.icon("back_button"), fallback: chevronLeftIcon }));
const commentButton = computed(() => cartCommentVariants[decor.variant("comment_button", CART_COMMENT_DEFAULT)] ?? cartCommentVariants[CART_COMMENT_DEFAULT]);
const openCommentEditor = () => {
	if (isCommentLocked.value) {
		if (!isCommentOverlay.value) return;
		cancelPayment();
	}
	is_ToggleComment_kso.value = true;
};
const commentControl = computed(
	(): CartCommentControl => ({
		label: comment.value.length > 0 ? t(`cart.button.comment.edit`) : generalComment.value?.fieldName || "",
		disabled: isCommentLocked.value && !isCommentOverlay.value,
		toggle: () => {
			if (isCommentLocked.value) return openCommentEditor();
			is_ToggleComment_kso.value = !is_ToggleComment_kso.value;
		},
		icon: decor.icon("comment_button"),
		fallback: chatIcon,
	}),
);
const loyaltyContentComponent = computed(
	() => cartLoyaltyVariants[decor.variant("loyalty_button", CART_LOYALTY_DEFAULT)] ?? cartLoyaltyVariants[CART_LOYALTY_DEFAULT],
);
const loyaltyControl = computed(
	(): CartLoyaltyControl => ({
		label: truncatedLoyaltyText.value,
		sticker: themeStore.data?.cart_loyalty_button?.cart_loyalty_button_sticker || null,
		stickerOffset: Math.ceil(stickerLoyalityWidth.value / 2),
		icon: decor.icon("loyalty_button"),
		fallback: qrScannerIcon,
	}),
);
const payButton = computed(() => cartPayVariants[decor.variant("pay_button", CART_PAY_DEFAULT)] ?? cartPayVariants[CART_PAY_DEFAULT]);
const payControl = computed(
	(): CartPayControl => ({
		label: buttonText.value,
		total: finalTotal.value,
		symbol: config_lib.value.currency_symbol,
		disabled: disabledStateBtn.value,
		action: buttonAction,
		icon: decor.icon("pay_button"),
		fallback: cartIcon,
	}),
);

const totalItemsCount = computed(() => cartProducts_lib.value?.reduce((sum, item) => sum + item.amount, 0) || 0);
const user_phone = computed(() => userStore.data?.phone);

watch(comment, (v) => {
	if (v?.trim()?.length) isCommentInvalid.value = false;
});

watch(
	() => cartProducts_lib.value.length,
	(len, prev) => {
		if (prev === undefined || prev === 0 || len <= prev) return;
		nextTick(() => {
			const el = cartCardsEl.value;
			if (!el) return;
			el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
		});
	},
);

watch([totalItemsCount, user_phone, () => storeCart.selectedOrderType], () => {
	if (totalItemsCount.value === 0) abortController = null;

	abortController?.abort();
	isButtonDisabled.value = false;
});

const resetAdult = () => {
	storeCart.isAdultConfirmed = false;
	cartProducts_lib.value = cartProducts_lib.value.filter((item) => !item.adult);
	storeMain.updateCartInLocalStorage();
};

const applyPromoCode = (code: string) => {
	if (abortController) abortController?.abort();
	resetCart();
	promoCode.value = code;
	isButtonDisabled.value = false;

	abortController = new AbortController();
	calculateDiscounts(true).catch(() => {
		// @TODO: кастомная обработка ошибки если friendly message не устроит
		promoCode.value = "";
	});
};

const resetPromoCode = () => {
	if (abortController) abortController?.abort();
	resetCart();
	promoCode.value = "";
	isButtonDisabled.value = false;
	disabledDiscountBtn.value = false;
};

const confirmAdult = () => {
	storeCart.isAdultConfirmed = true;
	buttonAction();
};

const confirmLoyaltyPlant = (code: string) => {
	if (abortController) abortController?.abort();
	resetCart();
	promoCode.value = code;
	isButtonDisabled.value = false;

	// @TODO: заделка на будущее для проверки кода и применения скидки
	// abortController = new AbortController();
	// calculateDiscounts(true);
};

const shouldShowOrderTypeTabs = computed(() => {
	if (tableStore.isTableService || tableStore.isTableTent) return false;

	const types = storeCart.orderTypes || [];
	return (props.configPage.kiosk_menu || props.configPage.qr_menu) && types.length > 1;
});

const fullDiscountReset = () => {
	discountSumma.value = 0;
	extraDiscount.value = 0;

	cartProducts_lib.value.forEach((item: any) => {
		if (item.new_price !== undefined) {
			item.price = item.new_price || item.original_price || item.price;
			delete item.new_price;
		}
		delete item.discounts;
		delete item.total_new_price;
		delete item.delta_price;
	});
	cartProducts_lib.value.forEach((item: any) => {
		if (item.is_combo || item.isCombo) {
			if (item.new_price !== undefined) {
				item.price = item.new_price || item.original_price || item.price;
				delete item.new_price;
			}
			delete item.discounts;
			delete item.total_new_price;
		}
	});
};

const resetCart = () => {
	isCreateButtonVisible.value = true;
	storeMain.setBonus(0);

	fullDiscountReset();

	cartProducts_lib.value.splice(0, cartProducts_lib.value.length, ...cartProducts_lib.value.map(storeMain.makeProductFullyReactive));
	discountSumma.value = 0;
	totalPrice_lib.value = null;
	extraDiscount.value = 0;
};

//Сброс корзины до значений по умолчанию
watch(
	[() => storeMain.cartProducts_lib, user_phone, totalItemsCount, () => storeCart.selectedOrderType, promoCode],
	(data) => {
		if (updateTrigger.value > 0 || isLoadingPromoCode.value) {
			updateTrigger.value--;
			return;
		}

		if (data && !disabledDiscountBtn.value) {
			// checkDiscountBtn()
			fullDiscountReset();
			isBonusActive.value = false;
			resetCart();

			updateTrigger.value = 1;
		}

		disabledDiscountBtn.value = false;
	},
	{ deep: true },
);

watch(
	() => storeCart.orderTypes,
	(types) => {
		if (!storeCart.selectedOrderType?.id && types?.length > 0) storeCart.selectedOrderType = types[0];
	},
	{ immediate: true, deep: true },
);

watch(
	() => userStore.data,
	() => {
		nextTick();
	},
	{ deep: true },
);
</script>

<template>
	<div class="cart_wrapper" v-bind="decor.bind('page', { empty: isCartEmpty })">
		<ModalCartPromoResetConfirm @confirm="resetPromoCode" />
		<ModalCartDeleteConfirm :has-promo-code="hasPromoCode" @confirm="deleteItem" />
		<ModalCartExtendConfirm @confirm="extendOrder" />
		<ModalCartRequiredEmail @confirm="makeInitRequest" />
		<ModalCartAdultConfirm @confirm="confirmAdult" />
		<ModalCartAdultReset @confirm="resetAdult" />
		<ModalLoyaltyPlantScanner @confirm="confirmLoyaltyPlant" />
		<PopupWindowDefault :visible="storeCart.isDrawerOpen" @update:visible="storeCart.isDrawerOpen = $event">
			<CardReader />
		</PopupWindowDefault>
		<SystemNotification />
		<header class="cart_header_content" v-bind="decor.bind('header', { paying: isPaying })">
			<div v-if="props.configPage.kiosk_menu" class="cart_header_subwrapper" v-bind="decor.bind('header_row')">
				<div v-if="!schedule.isNight" class="cart_kso_button_wrapper" v-bind="decor.bind('back_wrapper')">
					<div class="arrow_wrapper" :class="{arrow_wrapper_kso: props.configPage.kiosk_menu}" @click="goBack" v-bind="decor.bind('back_button')">
						<component :is="backContent" :control="backControl" />
					</div>
				</div>
				<div class="kso_btn_wrapper" v-if="generalComment?.show" v-bind="decor.bind('comment_wrapper')">
					<component
						:is="commentButton"
						v-if="!isCommentAlwaysOpen"
						:control="commentControl"
						:style="{ visibility: !is_ToggleComment_kso  && cartProducts_lib.length > 0 ? 'visible' : 'hidden' }"
						:class="{'button_kso_comment': configPage.kiosk_menu}"
						id="button_kso_comment"
						v-bind="decor.bind('comment_button', { filled: comment.length > 0 })"
					/>
				</div>
				<div :class="{ cart_header_wrapper: props.configPage.kiosk_menu }" v-bind="decor.bind('loyalty_wrapper')">
					<button
						v-if="(themeStore.data?.kiosk_show_loyalty_card_button || themeStore.data?.kiosk_show_loyalty_phone_input_button) && !props.config_qr"
						type="button"
						class="buttonDefaultWrapper button_payment_cart button_payment_cart_loyal"
						:style="{ marginRight: themeStore.data?.cart_loyalty_button?.cart_loyalty_button_sticker ? `${Math.ceil(stickerLoyalityWidth / 2 + 10)}px` : '', paddingRight: themeStore.data?.cart_loyalty_button?.cart_loyalty_button_sticker ? 0 : '' }"
						@click="props.loyaltyAction ? props.loyaltyAction() : storeCart.openDrawer()"
						v-bind="decor.bind('loyalty_button', { authorized: userStore.isAuthorized })"
					>
						<component :is="loyaltyContentComponent" ref="loyaltyContent" :control="loyaltyControl" />
					</button>
				</div>
				<button
					v-if="decor.visible('close_button', false)"
					type="button"
					class="cart_icon_button cart_close_button"
					:aria-label="t(`cart.button.back`)"
					@click="goBack"
					v-bind="decor.bind('close_button')"
				>
					<DecorIcon :icon="decor.icon('close_button')" :fallback="crossIcon" />
				</button>
			</div>
			<div
				v-if="decor.visible('nutrition')"
				:class="[config_qr ? 'cart_nutritions_qr_wrapper' : 'cart_nutritions_kso_wrapper']"
				v-bind="decor.bind('nutrition')"
			>
				<div v-if="config_qr" class="cart_qr_button_wrapper">
					<div class="arrow_wrapper" @click="goBack">
						<ArrowLeft class="cart_header_back_button" />
					</div>
				</div>
				<div
					v-if="themeStore.data?.show_nutrition_in_cart || isNightEmpty"
					class="cart_header"
					:style="{ paddingTop: !props.config_qr ? '10px': 0, paddingRight: !props.config_qr ? 0 : '10px' }"
				>
					<div class="cart_header_info">
						<p>
							<AnimNum :price="totalNutritions.energy_amount" :duration="800" />
						</p>
						<p class="cart_info_name">{{ t(`card.nutritional.calories`) }}</p>
					</div>
					<div class="cart_header_info">
						<p>
							<AnimNum :price="totalNutritions.proteins_amount" :duration="900" />
							{{ t(`cart.header.nutritional.symbol`) }}
						</p>
						<p class="cart_info_name">{{ t(`card.nutritional.proteins`) }}</p>
					</div>
					<div class="cart_header_info">
						<p>
							<AnimNum :price="totalNutritions.fat_amount" :duration="1000" />
							{{ t(`cart.header.nutritional.symbol`) }}
						</p>
						<p class="cart_info_name">{{ t(`card.nutritional.fats`) }}</p>
					</div>
					<div class="cart_header_info">
						<p>
							<AnimNum :price="totalNutritions.carbohydrates_amount" :duration="1100" />
							{{ t(`cart.header.nutritional.symbol`) }}
						</p>
						<p class="cart_info_name">{{ t(`card.nutritional.carbohydrates`) }}</p>
					</div>
				</div>
			</div>
			<div v-if="shouldShowOrderTypeTabs && decor.visible('order_tabs')" class="cart-tabs-wrapper" v-bind="decor.bind('order_tabs')">
				<ul class="cart-tabs-container">
					<li
						v-for="item in storeCart.orderTypes"
						:key="item.id"
						class="cart-tab"
						:data-active="item.id === storeCart.selectedOrderType?.id"
						@click="storeCart.selectedOrderType = item"
					>
						<motion.div v-if="item.id === storeCart.selectedOrderType?.id" class="cart-underline" layout-id="cart-underline" id="cart-underline" />
						<span>{{ item.name }}</span>
					</li>
				</ul>
			</div>
		</header>
		<template v-if="(isCommentAlwaysOpen || is_ToggleComment_kso) && !isCommentLocked && configPage.kiosk_menu && cartProducts_lib?.length">
			<div v-if="isCommentOverlay" class="cart_comment_veil" v-bind="decor.bind('comment_veil')" @click="closeCommentEditor" />
			<div class="wrapper_comment" v-bind="decor.bind('comment_editor')">
				<div v-if="isCommentOverlay" class="cart_comment_head" v-bind="decor.bind('comment_head')">
					<button type="button" class="cart_comment_back" :aria-label="t(`cart.button.back`)" v-bind="decor.bind('comment_back')" @click="closeCommentEditor">
						<DecorIcon :icon="decor.icon('comment_back')" :fallback="chevronLeftIcon" />
					</button>
					<p class="cart_comment_title" v-bind="decor.bind('comment_title')">{{ commentTitle }}</p>
					<button type="button" class="cart_comment_close" :aria-label="t(`cart.button.back`)" v-bind="decor.bind('comment_close')" @click="closeCommentEditor">
						<DecorIcon :icon="decor.icon('comment_close')" :fallback="crossIcon" />
					</button>
				</div>
				<div class="wrapper_limit_in_area" v-bind="decor.bind('comment_area')">
					<textarea
						ref="commentEl"
						class="comment_order"
						:placeholder="generalComment?.placeholder || t(`cart.input.comment.placeholder`)"
						name="commentKso"
						:maxlength="commentMaxLength"
						:aria-invalid="isCommentInvalid"
						v-model="comment"
						v-bind="decor.bind('comment_field')"
						@keydown.enter="onCommentEnter"
					/>
					<p v-if="decor.visible('comment_limit')" class="limit_comment" v-bind="decor.bind('comment_limit')">
						{{ `${comment.length} / ${commentMaxLength}` }}
					</p>
				</div>
				<div v-if="isCommentOverlay" class="cart_comment_meta" v-bind="decor.bind('comment_meta')">
					<span v-bind="decor.bind('comment_hint')">{{ t(`cart.comment.hint`) }}</span>
					<span v-bind="decor.bind('comment_counter')">{{ `${comment.length}/${commentMaxLength}` }}</span>
				</div>
				<div v-if="isCommentOverlay && commentChips.length" class="cart_comment_chips" v-bind="decor.bind('comment_chips')">
					<button
						v-for="chip in commentChips"
						:key="chip"
						type="button"
						class="cart_comment_chip"
						v-bind="decor.bind('comment_chip')"
						@pointerdown.prevent
						@click="appendCommentChip(chip)"
					>
						{{ chip }}
					</button>
				</div>
				<ButtonDefault
					v-if="!isCommentRequired && decor.visible('comment_save')"
					:textButtonDefault="t(`cart.button.comment.save`)"
					:onClick="saveCommentKso"
					class="button_bonus_cart"
					v-bind="decor.bind('comment_save')"
				/>
			</div>
		</template>
		<div
			v-if="(isCommentRequired || is_ToggleComment_qr) && !isCommentLocked && config_qr && cartProducts_lib?.length && !(tableStore.isTableService && tableStore.hasOrder)"
			class="wrapper_comment"
		>
			<div class="wrapper_limit_in_area">
				<textarea
					ref="commentEl"
					class="comment_order"
					:placeholder="generalComment?.placeholder || t(`cart.input.comment.placeholder`)"
					name="commentQr"
					:maxlength="commentMaxLength"
					:aria-invalid="isCommentInvalid"
					v-model="comment"
				/>
				<p class="limit_comment">{{ `${comment.length} / ${commentMaxLength}` }}</p>
			</div>
			<ButtonDefault v-if="!isCommentRequired" :textButtonDefault="t(`cart.button.comment.save`)" :onClick="saveCommentQr" class="button_bonus_cart" />
		</div>
		<div v-if="config_qr && generalComment?.show && !(tableStore.isTableService && tableStore.hasOrder)">
			<ButtonDefault
				v-if="!isCommentRequired && !is_ToggleComment_qr && cartProducts_lib.length > 0"
				:disabled="isCommentLocked"
				:textButtonDefault="comment.length > 0 ? t(`cart.button.comment.edit`) : generalComment.fieldName || ''"
				:onClick="() => is_ToggleComment_qr = !is_ToggleComment_qr "
				id="button_comments"
			/>
		</div>
		<div
			v-if="tableStore.isTableService && tableStore.data?.number.toString()"
			class="table_order_table_wrapper"
			@click="tableStore.order && router.push({ name: 'order-ts' })"
		>
			<p class="table_order_desk_number">{{ t(`cart.table.desk-number`, { number: tableStore.data?.number }) }}</p>
			<p v-if="tableStore.order?.id" class="table_order_indent_number">
				{{ t(`cart.table.order-number`, { number: tableStore.order.plugin_order?.number ?? '-' }) }}
			</p>
		</div>
		<div ref="cartCardsEl" class="cart_cards" v-bind="decor.bind('list', { paying: isPaying })">
			<template v-if="tableStore.hasOrder && tableStore.order?.items?.length && isOrderItemsVisible">
				<OrderBox v-for="item in tableStore.order.items" :key="item.idSystem" :cardItem="item" />
			</template>

			<CartBox
				v-for="item in cartProducts_lib"
				:key="item.idSystem"
				:cardItem="item"
				:is-qr="props.config_qr"
				:is-kiosk="props.configPage.kiosk_menu"
				:has-promo-code="hasPromoCode"
				:decoration="props.decoration"
			/>

			<NightScanEmpty v-if="isNightEmpty" />
			<div v-if="isCartEmpty && decor.visible('empty', false)" class="cart_empty" v-bind="decor.bind('empty')">
				<DecorIcon class="cart_empty_icon" :icon="decor.icon('empty_icon')" :fallback="cartIcon" v-bind="decor.bind('empty_icon')" />
				<p class="cart_empty_title" v-bind="decor.bind('empty_title')">{{ t(`cart.empty.title`) }}</p>
				<p class="cart_empty_subtitle" v-bind="decor.bind('empty_subtitle')">{{ t(`cart.empty.subtitle`) }}</p>
				<button type="button" class="cart_empty_button" @click="goBack" v-bind="decor.bind('empty_button')">{{ t(`cart.button.to-menu`) }}</button>
			</div>
		</div>
		<button
			v-if="isCommentLineVisible"
			type="button"
			class="cart_comment_line"
			:disabled="isCommentLocked && !isCommentOverlay"
			@click="openCommentEditor"
			v-bind="decor.bind('comment_line')"
		>
			<DecorIcon class="cart_comment_line_icon" :icon="decor.icon('comment_line_icon')" :fallback="chatIcon" v-bind="decor.bind('comment_line_icon')" />
			<span class="cart_comment_line_text" v-bind="decor.bind('comment_line_text')">{{ comment }}</span>
			<span class="cart_comment_line_action" v-bind="decor.bind('comment_line_action')">{{ t(`cart.comment.change`) }}</span>
		</button>
		<div v-if="isPaying && ksoStage !== 'creating' && ksoDiscount !== 0 && decor.visible('summary', false)" class="cart_summary" v-bind="decor.bind('summary')">
			<div class="cart_summary_row" v-bind="decor.bind('summary_row')">
				<span class="cart_summary_label" v-bind="decor.bind('summary_label')">{{ t(`cart.summary.subtotal`) }}</span>
				<span class="cart_summary_value" v-bind="decor.bind('summary_value')">{{ formatPrice(summarySubtotal, config_lib.currency_symbol) }}</span>
			</div>
			<div v-if="ksoDiscount !== 0" class="cart_summary_row" v-bind="decor.bind('summary_row', { discount: true })">
				<span class="cart_summary_label" v-bind="decor.bind('summary_label', { discount: true })">
					{{ ksoDiscount > 0 ? t(`cart.summary.supplement`) : t(`cart.summary.discount`) }}
				</span>
				<span v-if="summaryPercent > 0" class="cart_summary_badge" v-bind="decor.bind('summary_badge')">
					{{ `${ksoDiscount > 0 ? "+" : "−"}${summaryPercent}%` }}
				</span>
				<span class="cart_summary_value" v-bind="decor.bind('summary_value', { discount: true })">
					{{ `${ksoDiscount > 0 ? "+" : "−"}${formatPrice(Math.abs(ksoDiscount), config_lib.currency_symbol)}` }}
				</span>
			</div>
		</div>
		<div class="cart_footer_wrapper" :class="{ cart_footer_wrapper_kso_hidden: ksoStage !== 'hidden' }" v-bind="decor.bind('footer', { paying: isPaying })">
			<PromoField
				v-if="!tableStore.isTableService && themeStore.data?.cart_promo_code"
				:code="promoCode"
				:is-loading="isLoadingPromoCode"
				@submit="applyPromoCode"
				@reset="resetPromoCode"
			/>
			<p class="footer_title" v-if="isBonusActive && !isCreateButtonVisible && maxBonuses > 0">
				{{ t(`cart.footer.bonus.withdraw.prefix`) }}
				<input
					:type="props.configPage.kiosk_menu ? 'text' : 'number'"
					:inputmode="props.configPage.kiosk_menu ? 'none' : 'decimal'"
					:data-vk-layout="props.configPage.kiosk_menu ? 'numbers' : undefined"
					data-vk-lift
					placeholder="0"
					:style="{ maxWidth: `${countBonus.toString().length || 1}em`}"
					:disabled="!userStore.isAuthorized || userStore.data?.balance === 0"
					v-model.number="countBonus"
					@input="validateInput"
					min="0"
					:max="maxBonuses"
					step=".01"
					@keypress="preventNonNumeric"
				>
				{{ t(`cart.footer.bonus.withdraw.postfix`, { count: formatPrice(countBonus) }) }}
			</p>
			<p class="footer_sales" v-if="isBonusActive && !isCreateButtonVisible && maxBonuses > 0">
				{{ t(`cart.footer.bonus.balance`, { count: formatPrice(maxBonuses), balance: userStore.isAuthorized ? formatPrice(userStore.data?.balance ?? 0) : 0 }) }}
			</p>
			<p class="footer_sales" v-else-if="isBonusActive && !isCreateButtonVisible">{{ t(`cart.footer.bonus.balance.empty`) }}</p>
			<input
				type="range"
				class="footer_range"
				v-model.number="countBonus"
				:step="isBonusDecimalAllowed ? '.01' : '1'"
				:min="0"
				:max="maxBonuses"
				v-if="isBonusActive && !isCreateButtonVisible && maxBonuses > 0"
			>
			<ul v-if="decor.visible('totals')" class="list_price" v-bind="decor.bind('totals')">
				<li v-if="isBonusActive && !isCreateButtonVisible">
					<p>{{ t(`cart.footer.amount.title`) }}</p>
					<p>
						<AnimNum :price="totalPrice_lib" :symbol="config_lib.currency_symbol" />
					</p>
				</li>
				<li v-if="isBonusActive && !isCreateButtonVisible">
					<p>{{ t(`cart.footer.bonus.title`) }}</p>
					<p>
						<AnimNum :price="countBonus" :symbol="t(`cart.footer.bonus.sign`)" />
					</p>
				</li>
				<li v-if="extraDiscount !== 0">
					<p class="accept_field">{{ extraDiscount > 0 ? t(`cart.footer.supplement.title`) : t(`cart.footer.discount.title`) }} </p>
					<p class="accept_field">
						<AnimNum :price="Math.abs(extraDiscount)" :symbol="config_lib.currency_symbol" />
					</p>
				</li>
				<li v-else-if="discountSumma !== 0">
					<p class="accept_field">{{ discountSumma > 0 ? t(`cart.footer.supplement.title`) : t(`cart.footer.discount.title`) }} </p>
					<p class="accept_field">
						<AnimNum :price="Math.abs(discountSumma)" :symbol="config_lib.currency_symbol" />
					</p>
				</li>
				<li v-if="tableStore.isTableService && tableStore.order">
					<p>{{ t(`cart.footer.amount.title`) }}</p>
					<p>
						<AnimNum :price="tableStore.order.total_cost ?? 0" :symbol="config_lib.currency_symbol" />
					</p>
				</li>
				<li v-if="tableStore.isTableService && tableStore.order">
					<p>{{ t(`cart.footer.extend-amount.title`) }}</p>
					<p>
						<AnimNum :price="totalPrice_lib ?? 0" :symbol="config_lib.currency_symbol" />
					</p>
				</li>
				<li v-if="deliveryCost !== null">
					<p class="accept_field">{{ t(`cart.footer.delivery-cost.title`) }} </p>
					<p class="accept_field">
						<AnimNum :price="deliveryCost" :symbol="config_lib.currency_symbol" />
					</p>
				</li>
				<li>
					<p class="accept_field">{{ t(`cart.footer.final.title`) }}</p>
					<p v-if="tableStore.isTableService && tableStore.order" class="accept_field">
						<AnimNum :price="(tableStore.order.total_cost ?? 0) + totalPrice_lib" :symbol="config_lib.currency_symbol" />
					</p>
					<p v-else class="accept_field">
						<AnimNum :price="totalPrice_lib + deliveryCost - countBonus" :symbol="config_lib.currency_symbol" />
					</p>
				</li>
			</ul>
			<div v-if="schedule.isClosed || (props.configPage.qr_menu && schedule.isNight)" class="cart_schedule_notice">{{ schedule.disclaimerText }}</div>
			<div
				v-else-if="minOrderPrice && minOrderPrice > initialCartPrice_lib && !tableStore.hasOrder"
				class="cart_min_order_notice"
				v-bind="decor.bind('min_order_notice')"
			>
				{{ t(`cart.footer.minimal.notice`, { minimal: formatPrice(minOrderPrice), remaining: formatPrice(minOrderPrice - initialCartPrice_lib), currency: config_lib.currency_symbol }) }}
			</div>
			<div class="wrapper_footer_cart_btn" v-bind="decor.bind('footer_buttons')">
				<ButtonDefault
					:textButtonDefault="themeStore.data?.bonuses_button_name || t(`cart.button.bonuses`)"
					:onClick="toggleBonuses"
					class="button_bonus_cart"
					v-if="userStore.isAuthorized && !isCreateButtonVisible && !tableStore.isTableService"
					v-bind="decor.bind('bonus_button')"
				/>
				<component
					:is="payButton"
					:control="payControl"
					class="button_payment_cart"
					v-bind="decor.bind('pay_button', { empty: isCartEmpty, disabled: disabledStateBtn })"
				/>

				<!-- v-if="configPage.kiosk_menu" -->
				<!-- <ButtonDefault v-if="configPage.qr_menu" :textButtonDefault="textPaymentBtn" :onClick="paymentCart_qr" class="button_payment_cart" /> -->
				<!-- :disabled="isButtonDisabled" -->
			</div>
		</div>
		<div
			v-if="isPaying && decor.visible('paying_dim', false)"
			class="cart_paying_dim"
			v-bind="decor.bind('paying_dim', { creating: ksoStage === 'creating', methods: ksoStage === 'methods' })"
		/>
	</div>
	<CartCompletedOrder
		v-if="!tableStore.isTableService"
		:order-id="orderId"
		:is-kiosk="!props.config_qr"
		:decoration="props.decoration"
		@cancel-payment="cancelPayment"
	/>
	<!-- v-if="configPage.kiosk_menu" -->
	<!-- v-if="isOrderCompleted_lib && orderNumber" -->
	<NotificationWindow
		v-model:visible="is_notification_lib"
		:textNotificationDefault="notificationData_lib.textNotification"
		:backgroundColor="themeStore.data?.accent_color || '#FF6D2E'"
		:buttonAccept="null"
	/>
	<BottomSheet v-if="!isKsoFlow" v-model="is_paymentCart" class="product_card_sheet" :snap-points="['80%']">
		<CartPayment
			:config-page="configPage"
			:comment="formattedComment"
			:cart-items="paymentCartItems"
			:order-id="orderId"
			:signal="abortController?.signal"
			@selectPayment="handleSelectPayment"
			@close="is_paymentCart = false"
		/>
	</BottomSheet>
	<KsoCheckout
		v-if="isKsoFlow"
		:stage="ksoStage"
		:dock-to="props.ksoDock"
		:decoration="props.decoration"
		:order-ready="isKsoOrderReady"
		:discount="ksoDiscount"
		:bonuses="countBonus"
		:max-bonuses="maxBonuses"
		:total="ksoTotal"
		:delivery-cost="deliveryCost"
		:providers="providerTypes"
		:currency-symbol="config_lib.currency_symbol"
		:bonus-sign="t(`cart.footer.bonus.sign`)"
		:bonuses-label="themeStore.data?.bonuses_button_name || ''"
		:allow-decimal-bonuses="isBonusDecimalAllowed"
		:bonuses-available="isKsoBonusesAvailable"
		:pay-disabled="isButtonDisabled || disabledDiscountBtn"
		@apply-bonuses="applyKsoBonuses"
		@pay="openPaymentBox"
		@select-provider="selectKsoProvider"
		@close-methods="is_paymentCart = false"
		@cancel-creating="cancelKsoCreating"
		@cancel="cancelPayment"
		@bonuses-open="ksoBonusesRefreshTick++"
	/>
	<Teleport to="body">
		<Locator v-if="isLocatorSheetOpen" @close="isLocatorSheetOpen = false" @create-order="(locatorNumber) => createOrder(locatorNumber)" />
	</Teleport>
</template>

<style lang="scss">
  @use './cartPage.scss';
</style>
